import axios from "axios";
import i18n from "../i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "ne_access_token";

export const getStoredToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};
export const setStoredToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* ignorer (private mode etc.) */ }
};

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Fallback for in-app-browsere (Telegram, Instagram, Snapchat osv.) som
// noen ganger mister cookies mellom requests: send Authorization-header
// hvis vi har en lagret token. Backend støtter både cookie og Bearer.
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Response-interceptor: fang strukturert "EMAIL_NOT_VERIFIED"-feil og dispatch
// custom event som global modal (EmailGateModal) lytter på. Detalj kommer som
// dict { error_code, message, email } i FastAPI HTTPException.detail.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === "object" && detail.error_code === "EMAIL_NOT_VERIFIED") {
      try {
        window.dispatchEvent(new CustomEvent("ne:email-gate", { detail: { email: detail.email } }));
      } catch { /* SSR / Node */ }
    }
    // Skaper-abo utløpt: dispatch event så global modal kan vise kjøp-CTA
    if (detail && typeof detail === "object" && detail.code === "creator_access_expired") {
      try {
        window.dispatchEvent(new CustomEvent("ne:creator-expired", { detail: { message: detail.message, buyUrl: detail.buy_url } }));
      } catch { /* SSR / Node */ }
    }
    return Promise.reject(err);
  }
);

export const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Noe gikk galt. Prøv igjen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const formatRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  const t = (k, opts) => i18n.t(k, opts);
  const lang = i18n.language || "no";
  const dateLocale = lang === "no" ? "nb-NO" : lang === "sv" ? "sv-SE" : lang === "da" ? "da-DK" : "en-GB";
  if (diff < 60) return t("common.just_now");
  if (diff < 3600) return t("common.minutes_ago", { count: Math.floor(diff / 60) });
  if (diff < 86400) return t("common.hours_ago", { count: Math.floor(diff / 3600) });
  if (diff < 86400 * 7) return t("common.days_ago", { count: Math.floor(diff / 86400) });
  return d.toLocaleDateString(dateLocale, { day: "2-digit", month: "short", year: "numeric" });
};

export const creatorSlug = (nameOrHandle) => {
  // URL-vennlig slug: lowercase + åpenrom → bindestrek.
  // Brukes til /skaper/:slug-ruter. Backend godtar både slug og originalnavn.
  if (!nameOrHandle) return "";
  return String(nameOrHandle).trim().toLowerCase().replace(/\s+/g, "-");
};

export default api;
